(() => {
  // The Anchor Navigation section asks a person to paste a Shopify section id
  // into a text setting. That id carries the template id, which is different on
  // every theme and changes again whenever a page is duplicated, so the values
  // typed once go stale silently: the section's own handler cancels the click,
  // finds nothing, and the button feels dead.
  //
  // The stable part of the id is everything around the template number. This
  // repairs a link only when its target genuinely does not exist, by looking for
  // the one element whose id differs from the wanted one by that number alone.

  const linkSelector = '.js-anchor-link';
  const templatePattern = /template--\d+/;

  const escapeForRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const findByTemplateAgnosticId = (wantedId) => {
    if (!templatePattern.test(wantedId)) return null;

    const pattern = new RegExp(
      '^' + wantedId.split(templatePattern).map(escapeForRegExp).join('template--\\d+') + '$'
    );
    const matches = Array.from(document.querySelectorAll('[id]')).filter((element) =>
      pattern.test(element.id)
    );

    // More than one match means the guess is not safe, so the link is left alone.
    return matches.length === 1 ? matches[0] : null;
  };

  const repairLink = (link) => {
    const href = link.getAttribute('href') || '';

    if (href.charAt(0) !== '#' || href.length < 2) return;

    const wantedId = href.slice(1);

    if (document.getElementById(wantedId)) return;

    const target = findByTemplateAgnosticId(wantedId);

    if (target) link.setAttribute('href', '#' + target.id);
  };

  const repairAll = () => {
    document.querySelectorAll(linkSelector).forEach(repairLink);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', repairAll, { once: true });
  } else {
    repairAll();
  }

  // Sections are re-rendered while the theme editor is open, which replaces the
  // links and the ids together.
  document.addEventListener('shopify:section:load', repairAll);
})();

(() => {
  // A link to a form, such as #contact_form behind Request a Quote, lands on the
  // form's first field and hides the heading above it. When the page opens with
  // such a hash, the form gets a scroll margin as tall as the gap to its section
  // heading plus the header, which turns fixed once the page scrolls, and is
  // scrolled to again, once when the page is ready and once more after late
  // images above it have loaded and pushed it down, unless the visitor has
  // already scrolled on their own.

  const headingGap = 16;
  let visitorScrolled = false;

  ['wheel', 'touchstart', 'keydown'].forEach((type) =>
    window.addEventListener(type, () => { visitorScrolled = true; }, { once: true, passive: true })
  );

  const formFromHash = () => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    const target = id ? document.getElementById(id) : null;
    return target && target.tagName === 'FORM' ? target : null;
  };

  const landOnFormHeading = () => {
    const form = formFromHash();
    if (!form) return;

    const section = form.closest('.shopify-section');
    const heading = section ? section.querySelector('h1, h2, h3') : null;

    if (heading && heading.compareDocumentPosition(form) & Node.DOCUMENT_POSITION_FOLLOWING) {
      const gap = form.getBoundingClientRect().top - heading.getBoundingClientRect().top;
      const header = document.querySelector('sticky-header');
      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      if (gap > 0 && gap < window.innerHeight / 2) {
        form.style.scrollMarginTop = Math.round(gap + headerHeight + headingGap) + 'px';
      }
    }

    form.scrollIntoView({ block: 'start' });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', landOnFormHeading, { once: true });
  } else {
    landOnFormHeading();
  }

  window.addEventListener('load', () => {
    if (!visitorScrolled) landOnFormHeading();
  }, { once: true });

  window.addEventListener('hashchange', landOnFormHeading);
})();
